import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import StaffingDestinationSchema, {
  StaffingDestinationJSON,
  StaffingDestinationKind,
  StaffingDestinationMilestone,
  StaffingDestinationParams,
} from "work/staffing/_schemas/staffing-destination/staffing-destination-schema";

/** What the picker calls a live negotiation. */
export const PROPOSAL_DESTINATION_LABEL = "Proposal";

/** What the picker calls hired work that is still running. */
export const WORK_AGREEMENT_DESTINATION_LABEL = "Active work agreement";

/**
 * What the Counterparty column says where the server had nobody to name.
 *
 * A proposal that has not been addressed to a vendor yet has no other side, and neither does a row
 * whose organization the entity store could not resolve. Both are worth saying: a blank cell reads
 * as a column that failed to render.
 */
export const NO_COUNTERPARTY_YET = "Not addressed yet";

/**
 * One piece of work the signed-in viewer may put somebody onto, as the Add to Work picker offers it.
 *
 * The server decides every part of that sentence -- which work appears at all, which kind each one
 * is, and whether the person being looked at is already on its team. Nothing here re-derives any of
 * it, because a picker that worked out eligibility from a broader list would be offering what the
 * API has not agreed to.
 */
export default class StaffingDestination {
  public static readonly EXAMPLE: StaffingDestination = StaffingDestination.create({
    id: Guid.generate(),
    kind: "Proposal",
    name: "Opposition filings",
    counterpartyName: "Northwind Patents",
    activeSince: new AHDate("2026-03-04T09:30:00.000Z"),
    activeSinceEvent: "Created",
    candidateAlreadyBelongs: false,
  });

  public static create(params: StaffingDestinationParams): StaffingDestination {
    const parsed = StaffingDestinationSchema.validate(params);
    if (isNullOrUndefined(parsed)) {
      throw new Error("Invalid StaffingDestination parameters.");
    }
    return new StaffingDestination(parsed);
  }

  public static fromJSON(json: StaffingDestinationJSON): StaffingDestination {
    const params = StaffingDestinationSchema.deserialize(json);
    if (isNullOrUndefined(params)) {
      throw new Error("Invalid StaffingDestination JSON.");
    }
    return new StaffingDestination(params);
  }

  public static fromJSONArray(jsonArray: StaffingDestinationJSON[]): StaffingDestination[] {
    return jsonArray.map((json) => StaffingDestination.fromJSON(json));
  }

  public readonly id: Guid;
  public readonly kind: StaffingDestinationKind;
  public readonly name: string;
  public readonly counterpartyName: string;
  public readonly activeSince: AHDate;
  public readonly activeSinceEvent: StaffingDestinationMilestone;
  public readonly candidateAlreadyBelongs: boolean;

  /**
   * Which kind of work this is, in the words the picker shows.
   *
   * Every entry says it, including when the list holds only one kind. Which team somebody joins,
   * and whether they can be taken off it again without both parties agreeing, differ between the
   * two, and a name alone does not tell them apart.
   */
  public get kindLabel(): string {
    return this.kind === "Proposal" ? PROPOSAL_DESTINATION_LABEL : WORK_AGREEMENT_DESTINATION_LABEL;
  }

  /** The other organization, or the sentence that says there is not one yet. */
  public get counterpartyLabel(): string {
    return this.counterpartyName.trim().length > 0 ? this.counterpartyName : NO_COUNTERPARTY_YET;
  }

  /**
   * When this became work the viewer could staff, with the verb that says which event it was.
   *
   * The verb travels with the date because the two kinds do not share an event: a proposal was
   * drafted, an agreement was hired. One column sorts them because they are comparable instants;
   * naming the event is what stops the column presenting them as the same fact.
   */
  public get activeSinceLabel(): string {
    return `${this.activeSinceEvent} ${this.activeSince.asFormattedString}`;
  }

  public constructor(params: StaffingDestinationParams) {
    this.id = params.id;
    this.kind = params.kind;
    this.name = params.name;
    this.counterpartyName = params.counterpartyName;
    this.activeSince = params.activeSince;
    this.activeSinceEvent = params.activeSinceEvent;
    this.candidateAlreadyBelongs = params.candidateAlreadyBelongs;
  }

  public toJSON(): StaffingDestinationJSON {
    return StaffingDestinationSchema.serialize(this);
  }

  public isEqualTo(other: StaffingDestination): boolean {
    return (
      this.id.isEqualTo(other.id) &&
      this.kind === other.kind &&
      this.name === other.name &&
      this.counterpartyName === other.counterpartyName &&
      this.activeSince.isEqualTo(other.activeSince) &&
      this.activeSinceEvent === other.activeSinceEvent &&
      this.candidateAlreadyBelongs === other.candidateAlreadyBelongs
    );
  }

  public toString(): string {
    return `StaffingDestination { id: ${this.id.toString()}, kind: ${this.kind}, name: ${this.name}, counterparty: ${this.counterpartyName}, activeSince: ${this.activeSinceLabel}, candidateAlreadyBelongs: ${this.candidateAlreadyBelongs} }`;
  }
}
